import React from 'react';
import { SPRITES, VARIANTS, VARIANT_ORDER, RARITIES, MAX_LEVEL } from '../mock';
import { Progress } from '../components/ui/progress';
import { Crown, Star, Trophy } from 'lucide-react';

export default function StatsBar({ collection, sprites = SPRITES, showUnreleased = false, season = 'all' }) {
  // Season filter: only count sprites for the currently-selected season so
  // the OWNED / LEVELS / MASTERED progress reflects that season's ceiling.
  const seasonPool = season === 'all' ? sprites : sprites.filter((s) => (s.season || 'C7S3') === season);
  // When "Show Unreleased" is on, expand the counting pool to include
  // unreleased sprites so the OWNED / LEVELS / MASTERED totals grow to
  // reflect the full collection ceiling (e.g. 82 instead of 61).
  const pool = showUnreleased ? seasonPool : seasonPool.filter((s) => !s.unreleased);
  const totalSprites = pool.length;
  const totalLevels = totalSprites * MAX_LEVEL;

  const ownedCount = pool.filter((s) => collection[s.id]?.owned).length;
  const masteredCount = pool.filter((s) => collection[s.id]?.mastered).length;
  const levelsSum = pool.reduce((acc, s) => acc + (collection[s.id]?.level || 0), 0);
  const pct = totalSprites ? Math.round((ownedCount / totalSprites) * 100) : 0;
  const masterPct = totalSprites ? Math.round((masteredCount / totalSprites) * 100) : 0;
  const levelPct = totalLevels ? Math.round((levelsSum / totalLevels) * 100) : 0;

  const variantCounts = VARIANT_ORDER.map((v) => {
    const total = pool.filter((s) => s.variant === v).length;
    const owned = pool.filter((s) => s.variant === v && collection[s.id]?.owned).length;
    return { variant: v, total, owned };
  });

  const rarityCounts = Object.keys(RARITIES).map((r) => {
    const total = pool.filter((s) => s.rarity === r).length;
    const owned = pool.filter((s) => s.rarity === r && collection[s.id]?.owned).length;
    return { rarity: r, total, owned };
  });

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 mt-2 sm:mt-4 relative z-10">
      <div
        className="rounded-2xl sm:rounded-3xl p-2 sm:p-4 md:p-5 border-[3px] shadow-[0_6px_0_var(--t-card-border),0_20px_50px_-12px_rgba(0,0,0,0.4)]"
        style={{ background: 'var(--t-card)', borderColor: 'var(--t-card-border)' }}
      >
        {/* Top 3 big stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-2 sm:mb-4">
          {/* Owned */}
          <div className="rounded-xl sm:rounded-2xl p-2 sm:p-3 bg-gradient-to-br from-[#fff1ad] to-[#ffd900] border-[3px] shadow-[0_4px_0_var(--t-card-border)]" style={{ borderColor: 'var(--t-card-border)' }}>
            <div className="flex items-center gap-1 sm:gap-2 text-[#3a1064] font-black uppercase tracking-wider text-[10px] sm:text-xs">
              <Trophy className="h-3 w-3 sm:h-4 sm:w-4" /> Owned
            </div>
            <div className="flex items-end gap-1 sm:gap-2 mt-0.5 flex-wrap">
              <div className="text-xl sm:text-3xl font-black text-[#3a1064] leading-none">{ownedCount}</div>
              <div className="text-[11px] sm:text-sm font-black text-[#3a1064]/70 mb-0.5">/ {totalSprites}</div>
            </div>
            <div className="mt-1 sm:mt-1.5">
              <Progress value={pct} className="h-1 sm:h-1.5 bg-white/50 [&>div]:bg-[#3a1064]" />
              <div className="mt-0.5 text-[9px] sm:text-[11px] font-black text-[#3a1064]">{pct}%</div>
            </div>
          </div>

          {/* Levels */}
          <div className="rounded-xl sm:rounded-2xl p-2 sm:p-3 bg-gradient-to-br from-[#ff7ae0] to-[#ff2bd6] border-[3px] shadow-[0_4px_0_var(--t-card-border)]" style={{ borderColor: 'var(--t-card-border)' }}>
            <div className="flex items-center gap-1 sm:gap-2 text-white font-black uppercase tracking-wider text-[10px] sm:text-xs">
              <Star className="h-3 w-3 sm:h-4 sm:w-4" fill="currentColor" /> Levels
            </div>
            <div className="flex items-end gap-1 sm:gap-2 mt-0.5 flex-wrap">
              <div className="text-xl sm:text-3xl font-black text-white leading-none">{levelsSum}</div>
              <div className="text-[11px] sm:text-sm font-black text-white/80 mb-0.5">/ {totalLevels}</div>
            </div>
            <div className="mt-1 sm:mt-1.5">
              <Progress value={levelPct} className="h-1 sm:h-1.5 bg-white/30 [&>div]:bg-yellow-300" />
              <div className="mt-0.5 text-[9px] sm:text-[11px] font-black text-white">{levelPct}%</div>
            </div>
          </div>

          {/* Mastered */}
          <div className="rounded-xl sm:rounded-2xl p-2 sm:p-3 bg-gradient-to-br from-[#7cd9ff] to-[#00b8ff] border-[3px] shadow-[0_4px_0_var(--t-card-border)]" style={{ borderColor: 'var(--t-card-border)' }}>
            <div className="flex items-center gap-1 sm:gap-2 text-white font-black uppercase tracking-wider text-[10px] sm:text-xs">
              <Crown className="h-3 w-3 sm:h-4 sm:w-4" fill="currentColor" /> Mastered
            </div>
            <div className="flex items-end gap-1 sm:gap-2 mt-0.5 flex-wrap">
              <div className="text-xl sm:text-3xl font-black text-white leading-none">{masteredCount}</div>
              <div className="text-[11px] sm:text-sm font-black text-white/80 mb-0.5">/ {totalSprites}</div>
            </div>
            <div className="mt-1 sm:mt-1.5">
              <Progress value={masterPct} className="h-1 sm:h-1.5 bg-white/30 [&>div]:bg-[#3a1064]" />
              <div className="mt-0.5 text-[9px] sm:text-[11px] font-black text-white">{masterPct}%</div>
            </div>
          </div>
        </div>

        {/* Variant + Rarity rows */}
        <div className="grid md:grid-cols-2 gap-2 sm:gap-3">
          <div>
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider mb-1 sm:mb-1.5" style={{ color: 'var(--t-text)' }}>By Variant</div>
            <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
              {variantCounts.map((vc) => {
                const meta = VARIANTS[vc.variant];
                return (
                  <div
                    key={vc.variant}
                    className="rounded-lg p-1.5 sm:p-2 border-2 shadow-[0_2px_0_var(--t-card-border)]"
                    style={{
                      background: `linear-gradient(135deg, ${meta.color}66, ${meta.color}cc)`,
                      borderColor: 'var(--t-card-border)',
                    }}
                  >
                    <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#3a1064]">
                      {meta.label}
                    </div>
                    <div className="font-black text-[#3a1064] text-xs sm:text-sm leading-tight">
                      {vc.owned}<span className="text-[#3a1064]/60 font-bold text-[10px] sm:text-xs">/{vc.total}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider mb-1 sm:mb-1.5" style={{ color: 'var(--t-text)' }}>By Rarity</div>
            <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
              {rarityCounts.map((rc) => {
                const meta = RARITIES[rc.rarity];
                return (
                  <div
                    key={rc.rarity}
                    className="rounded-lg p-1.5 sm:p-2 border-2 shadow-[0_2px_0_var(--t-card-border)]"
                    style={{
                      background: `linear-gradient(135deg, ${meta.color}66, ${meta.color}cc)`,
                      borderColor: 'var(--t-card-border)',
                    }}
                  >
                    <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-[#3a1064]">
                      {meta.label}
                    </div>
                    <div className="font-black text-[#3a1064] text-xs sm:text-sm leading-tight">
                      {rc.owned}<span className="text-[#3a1064]/60 font-bold text-[10px] sm:text-xs">/{rc.total}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
